<template>
    <vx-expandable-card>
        <template v-slot:header>
            <div class="flex items-baseline gap-3">
                Effective At
                <span class="text-sm font-normal text-slate-700" data-test="effective-at-timezone">
                    <template v-if="effectiveAtFormatted">{{ effectiveAtFormatted }}</template>
                    <template v-else>Times are shown in {{ timezoneLabel }}</template>
                </span>
            </div>
        </template>
        <template v-slot:content>
            <vx-date-time-picker
                v-model="effectiveAtDate"
                :timezone="userTimezone"
                :minDate="minEffectiveDate"
                :disabled="!props.canEdit"
                :error="displayFirstError(validation.effective_at.$errors)"
                isRequired
                aria-label="Effective at"
                data-test="effective-at-picker"
            />
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxDateTimePicker, VxExpandableCard } from '@voxie/frontend-components';
import { computed } from 'vue';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { displayFirstError, isAfterDate } from '~/utils/validation';
import dayjs from '~/utils/dayjs';
import { formatTz, getUserTz } from '~/utils/date';
import type { TermFormData } from '~/services/adminTermsClient.types';

const props = defineProps<{
    canEdit: boolean;
}>();

const data = defineModel<TermFormData>({ required: true });

const userTimezone = getUserTz();
const minEffectiveDate = computed(() => dayjs().tz(userTimezone).toDate());

const timezoneLabel = computed(() => formatTz(dayjs().tz(userTimezone).format('z')));

const effectiveAtFormatted = computed(() => {
    if (!data.value.effective_at) {
        return '';
    }

    const parsed = dayjs.utc(data.value.effective_at);

    if (!parsed.isValid()) {
        return '';
    }

    const date = parsed.tz(userTimezone);

    return `${date.format('MMM DD, YYYY [at] h:mm a')} ${formatTz(date.format('z'))}`;
});

const effectiveAtDate = computed<Date | undefined>({
    get() {
        if (!data.value.effective_at) {
            return undefined;
        }
        const parsed = dayjs.utc(data.value.effective_at);
        return parsed.isValid() ? parsed.toDate() : undefined;
    },
    set(newValue) {
        data.value.effective_at = newValue ? dayjs(newValue).toISOString() : '';
    },
});

const validation = useVuelidate(
    {
        effective_at: { required, isAfter: isAfterDate({ required: true }) },
    },
    data
);
</script>
